/**
 * ステージ遷移時の自社広告インタースティシャル（TK-018、FR-022、ADR-013）。
 *
 * 次ステージへの導線（`Next Stage` / シリーズ選択へ）のクリックを一度だけ横取りし、
 * 姉妹サイトへの誘導を全画面オーバーレイで挟む。内容は設定（`promo.ts`）由来の静的な
 * テキストとリンクのみで、表示に伴うネットワーク往復はない。
 *
 * 導線を塞がない規則:
 * - 進行ボタン（元リンクと同じ遷移先）を最も目立つ操作として置く
 * - 閉じる（×）と Escape でその場に留まれる。閉じた後のクリックは素通しで、二度と挟まない
 * - 修飾キー付き・中ボタンのクリックは横取りしない（新しいタブで開く等の既定挙動を守る）
 * - JS が動かない環境では元リンクがそのまま機能する（横取り自体が発生しない）
 *
 * 導線はクリア演出後にしか現れないため（FR-015）、プレイ中の盤面領域を占有することはない。
 */

import { type InterstitialConfig, isPlainActivation } from './promo';

/** オーバーレイを組み立てて body に載せる。戻り値は撤去関数（テスト・閉じる操作用）。 */
function openInterstitial(
  nextHref: string,
  nextLabel: string,
  config: InterstitialConfig,
): () => void {
  const overlay = document.createElement('div');
  overlay.className = 'interstitial';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', '自社広告');

  const card = document.createElement('div');
  card.className = 'interstitial-card';

  const badge = document.createElement('p');
  badge.className = 'interstitial-badge';
  badge.textContent = '広告';

  const headline = document.createElement('p');
  headline.className = 'interstitial-headline';
  headline.textContent = config.headline;

  const promo = document.createElement('a');
  promo.className = 'interstitial-promo';
  promo.href = config.url;
  promo.target = '_blank';
  promo.rel = 'noopener';
  promo.textContent = config.linkLabel;

  const proceed = document.createElement('a');
  proceed.className = 'interstitial-continue';
  proceed.href = nextHref;
  proceed.textContent = nextLabel;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'interstitial-close';
  close.setAttribute('aria-label', '広告を閉じる');
  close.textContent = '×';

  card.append(badge, headline);
  if (config.body !== undefined) {
    const body = document.createElement('p');
    body.className = 'interstitial-body';
    body.textContent = config.body;
    card.append(body);
  }
  card.append(promo, proceed, close);
  overlay.append(card);

  const dismiss = (): void => overlay.remove();
  close.addEventListener('click', dismiss);
  overlay.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') dismiss();
  });

  document.body.append(overlay);
  // 進行が主目的の画面なので、フォーカスは次ステージへの導線に置く。
  proceed.focus();
  return dismiss;
}

/**
 * 導線リンクにインタースティシャルを取り付ける。`config.enabled` が偽なら何もしない。
 *
 * 表示は**ページにつき一度だけ**。進行・閉じるのどちらで抜けても、以降のクリックは
 * 元リンクの既定挙動に戻る（同じ広告を繰り返し挟んで導線を塞がない）。
 */
export function attachInterstitial(link: HTMLAnchorElement, config: InterstitialConfig): void {
  if (!config.enabled) return;
  let shown = false;
  link.addEventListener('click', (event) => {
    if (shown || !isPlainActivation(event)) return;
    shown = true;
    event.preventDefault();
    // .href は絶対 URL に解決されるため、元リンクの属性値（相対 URL）をそのまま引き継ぐ。
    const href = link.getAttribute('href') ?? link.href;
    openInterstitial(href, link.textContent ?? 'Next Stage', config);
  });
}
